import { cn } from '@fruuit/ui'
import { FlaskConical, Info, Rocket } from 'lucide-react'
import type { ReactNode } from 'react'
import type { CalloutMode } from './types'

/**
 * The handbook's two signature callouts, a small variant of `@fruuit/ui` Callout:
 * - demo: amber, what is simplified or simulated in this deployment
 * - production: teal, what the production build changes and what must happen before real data
 * The mode is spelled out in a label as well as by colour (never colour alone).
 */
const MODES: Record<CalloutMode, { label: string; box: string; badge: string; icon: ReactNode }> = {
  demo: {
    label: 'Demo mode',
    box: 'border-amber-line bg-amber-soft/60 text-amber-ink',
    badge: 'border-amber-line bg-amber-soft text-amber-ink',
    icon: <FlaskConical />,
  },
  production: {
    label: 'In production',
    box: 'border-teal bg-teal-50 text-teal-900',
    badge: 'border-teal bg-teal text-white',
    icon: <Rocket />,
  },
  note: {
    label: 'Note',
    box: 'border-leaf bg-leaf-soft/70 text-[#3c4d31]',
    badge: 'border-leaf bg-leaf-soft text-[#3c4d31]',
    icon: <Info />,
  },
}

export function modeLabel(mode: CalloutMode): string {
  return MODES[mode].label
}

export interface ModeCalloutProps {
  mode: CalloutMode
  title?: string
  children: ReactNode
  className?: string
}

export function ModeCallout({ mode, title, children, className }: ModeCalloutProps) {
  const style = MODES[mode]
  return (
    // A note, not an aside: a page has many callouts, and repeated complementary landmarks
    // with the same name aren't distinguishable (axe landmark-unique, Q-03).
    <div
      role="note"
      data-mode={mode}
      aria-label={title ? `${style.label}: ${title}` : style.label}
      className={cn(
        'docs-callout rounded-md border-l-[3px] px-4 py-3.5 text-[15px] leading-relaxed break-inside-avoid',
        style.box,
        className,
      )}
    >
      <p className="flex flex-wrap items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-sm border px-1.5 py-px text-[11px] font-semibold uppercase tracking-[0.08em]',
            style.badge,
          )}
        >
          <span aria-hidden="true" className="inline-flex [&>svg]:size-3.5">
            {style.icon}
          </span>
          {style.label}
        </span>
        {title ? <span className="font-semibold">{title}</span> : null}
      </p>
      <div className="mt-2 flex flex-col gap-2 [&_li]:ml-4.5 [&_li]:list-disc [&_li]:pl-1 [&_ul]:flex [&_ul]:flex-col [&_ul]:gap-1">
        {children}
      </div>
    </div>
  )
}
